// Station lookup for the Plan sheet's From/To fields.
//
// A plain `name.includes(query)` does not find the station a rider is typing, because MTA's
// complex names are written in signage abbreviations: "World Trade Center" is stored as
// `Chambers St/WTC/Park Place/Cortlandt St`, so the only row that query used to produce was
// the geocoded ADDRESS. Stations have to win that race — a rider planning a trip means the
// station — so matching here expands the abbreviations MTA actually uses in these names and
// ranks what it finds, and PlaceField lists the result above any address.
import type { StopEntry } from '../../store/stops'
import { compareRoutes } from '../../ui/colors'

// Only the abbreviations that appear in the live `/api/stops/with-routes` names, plus the
// compass initials. Expansion is one-way (short → long) because prefix matching already
// covers the other direction: "wash" is a prefix of "washington", but "wtc" is a prefix of
// nothing. `st` → `street` is wrong for "St George" and that is harmless — expansions are
// added as extra forms, never substituted for the name itself.
const ABBREVIATIONS: Record<string, string> = {
  wtc: 'world trade center',
  jfk: 'john f kennedy airport',
  av: 'avenue',
  avs: 'avenues',
  st: 'street',
  sts: 'streets',
  sq: 'square',
  ctr: 'center',
  hts: 'heights',
  pkwy: 'parkway',
  blvd: 'boulevard',
  hwy: 'highway',
  rd: 'road',
  pl: 'place',
  ft: 'fort',
  wash: 'washington',
  n: 'north',
  s: 'south',
  e: 'east',
  w: 'west',
}

export function normalizeStationText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

/** Every token replaced by its spelled-out form: "barclays ctr" → "barclays center". */
function expandPhrase(normalized: string): string {
  if (!normalized) return normalized
  return normalized
    .split(' ')
    .map((t) => ABBREVIATIONS[t] ?? t)
    .join(' ')
}

/**
 * The forms a name can be matched against, exactly or by prefix: the whole name, and each
 * `/`- or `-`-separated segment ("Atlantic Av-Barclays Ctr" is two places on one platform),
 * each in both its written and its spelled-out form.
 */
function nameForms(name: string): string[] {
  const forms = new Set<string>()
  for (const raw of [name, ...name.split(/[/-]/)]) {
    const norm = normalizeStationText(raw)
    if (!norm) continue
    forms.add(norm)
    forms.add(expandPhrase(norm))
  }
  return [...forms]
}

/** One string every token of the query has to be findable in, abbreviations included. */
function haystack(name: string): string {
  const norm = normalizeStationText(name)
  const extra: string[] = []
  for (const t of norm.split(' ')) {
    const full = ABBREVIATIONS[t]
    if (full) extra.push(full)
  }
  return extra.length > 0 ? `${norm} ${extra.join(' ')}` : norm
}

function containsWordPrefix(hay: string, token: string): boolean {
  if (!token) return false
  if (hay.startsWith(token)) return true
  return hay.includes(` ${token}`)
}

/**
 * How well `name` answers `query`, lower is better; `null` for no match at all.
 *   0 — the name, or one of its segments, IS the query ("herald sq" → 34 St-Herald Sq)
 *   1 — the name, or one of its segments, starts with it ("world trade" → the WTC complex)
 *   2 — the whole query appears inside it, at a word boundary
 *   3 — every word of the query appears somewhere, in any order
 */
export function stationMatchScore(name: string, query: string): number | null {
  const q = normalizeStationText(query)
  if (!q) return null
  const qx = expandPhrase(q)
  const forms = nameForms(name)

  for (const form of forms) if (form === q || form === qx) return 0
  // A plain prefix, not a whole-word one: the query is being typed, so "34 st-her" is a
  // prefix of "34 St-Herald Sq" and belongs above a station that merely contains it.
  for (const form of forms) {
    if (form.startsWith(q) || form.startsWith(qx)) return 1
  }
  const hay = haystack(name)
  if (containsWordPrefix(hay, q) || containsWordPrefix(hay, qx)) return 2
  const tokens = qx.split(' ').filter(Boolean)
  if (tokens.length > 1 && tokens.every((t) => containsWordPrefix(hay, t))) return 3
  return null
}

export interface StationMatch {
  id: string
  name: string
  lat: number
  lon: number
  routes: string[]
}

/**
 * Matching stations, best first, one row per name. Complexes are several GTFS stations
 * sharing one name; four identical rows help nobody, so they merge into one row carrying the
 * union of their routes and any member id — the planner walks the complex's transfer edges,
 * so every member routes identically. Merging happens BEFORE the cap, or a `limit` reached
 * early hides the station the rider actually typed.
 */
export function searchStations(stops: StopEntry[], query: string, limit = 6): StationMatch[] {
  const byName = new Map<string, { match: StationMatch; score: number }>()
  for (const s of stops) {
    const score = stationMatchScore(s.name, query)
    if (score === null) continue
    const existing = byName.get(s.name)
    if (existing) {
      existing.match.routes = [...new Set([...existing.match.routes, ...s.routes])].sort(
        compareRoutes,
      )
      continue
    }
    byName.set(s.name, {
      score,
      match: { id: s.id, name: s.name, lat: s.lat, lon: s.lon, routes: [...s.routes] },
    })
  }
  return [...byName.values()]
    // A shorter name matching equally well is the more specific answer: "Chambers St" over
    // "Chambers St/WTC/Park Place/Cortlandt St" for the query "chambers st".
    .sort((a, b) => a.score - b.score || a.match.name.length - b.match.name.length ||
      a.match.name.localeCompare(b.match.name))
    .slice(0, limit)
    .map((e) => e.match)
}
